import React from 'react';
import { useTranslation } from 'react-i18next';
import type { VideoComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { resolveVideoSrc } from '../../../utils/videoEmbed';

interface VideoSettingsProps {
  component: VideoComponent;
}

export const VideoSettings: React.FC<VideoSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();
  const resolved = resolveVideoSrc(component.settings.src, {
    autoplay: component.settings.autoplay,
    loop: component.settings.loop,
    controls: component.settings.controls,
  });

  const handleUpdate = (updates: Partial<VideoComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.video.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="default">{t('settings.common.styles.default')}</option>
          <option value="rounded">{t('settings.common.styles.rounded')}</option>
          <option value="card">{t('settings.common.styles.cardShadow')}</option>
          <option value="theater">{t('settings.common.styles.theater')}</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.video.videoUrl')}</label>
        <input
          type="text"
          value={component.settings.src}
          onChange={(e) => handleUpdate({ src: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.video.videoUrlPlaceholder')}
        />
        <p className="text-xs text-gray-500 mt-2">
          {t('settings.video.videoUrlHint', {
            defaultValue: 'วางลิงก์ YouTube, Shorts, youtu.be หรือไฟล์ .mp4 ได้เลย',
          })}
        </p>
        {component.settings.src?.trim() && resolved.kind === 'empty' && (
          <p className="text-xs text-red-600 mt-1">
            {t('settings.video.videoUrlInvalid', {
              defaultValue: 'ลิงก์นี้เล่นไม่ได้ — ตรวจว่าเป็น YouTube ที่เปิดสาธารณะ',
            })}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.video.width')}</label>
        <input
          type="text"
          value={component.settings.width}
          onChange={(e) => handleUpdate({ width: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.video.widthPlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.video.height')}</label>
        <input
          type="text"
          value={component.settings.height}
          onChange={(e) => handleUpdate({ height: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.video.heightPlaceholder')}
        />
      </div>

      <div className="space-y-3">
        <ToggleSwitch
          label={t('settings.video.autoplay')}
          checked={component.settings.autoplay}
          onChange={(checked) => handleUpdate({ autoplay: checked })}
        />

        <ToggleSwitch
          label={t('settings.video.showControls')}
          checked={component.settings.controls}
          onChange={(checked) => handleUpdate({ controls: checked })}
        />

        <ToggleSwitch
          label={t('settings.video.loop')}
          checked={component.settings.loop}
          onChange={(checked) => handleUpdate({ loop: checked })}
        />
      </div>
    </div>
  );
};
